import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import ExpandableFormList from "../gdk/ExpandableForm/ExpandableFormList";
import ExpandableFormListItem from "../gdk/ExpandableForm/ExpandableFormListItem";
import EmailForm from './EmailForm';
import '../../styles/SweepBranchEmail.css';
import { updateSweepDetails } from "../../api/agency-profile";
import { logDetails } from "../../api/logger-configuration";
import Toast, { ToastTypes } from "../gdk/Toast/Toast";
import PropTypes from 'prop-types';
import DeleteEmailModal from './DeleteEmailModal';

const BranchEmailManagement = ({ agencyProfile, setAgencyBranches, resetTrigger }) => {
    const [branchEmailExpandableForm, setBranchEmailExpandableForm] = useState();
    const [showDeleteBranchEmailModal, setShowDeleteBranchEmailModal] = useState(false);
    const [emailToDelete, setEmailToDelete] = useState('');
    const [isDeleting, setIsDeleting] = useState(false);
    const [editingEmail, setEditingEmail] = useState(null); // null, 'primary', 'add-new', or 'additional-{index}'

    // Memoized computed values
    const iconsDisabled = useMemo(() => editingEmail !== null, [editingEmail]);
    const additionalEmails = useMemo(() =>
        agencyProfile?.sweepDetails?.AdditionalSweepEmails || [],
        [agencyProfile?.sweepDetails?.AdditionalSweepEmails]
    );
    const branchUpdateConfirmationToastRef = useRef(null);
    const branchAddConfirmationToastRef = useRef(null);
    const branchDeleteConfirmationToastRef = useRef(null);

    // Memoized handlers
    const handleSave = useCallback((emailType) => {
        setEditingEmail(null);
        if (emailType === 'add-new' && branchAddConfirmationToastRef.current) {
            branchAddConfirmationToastRef.current.show();
        } else if (branchUpdateConfirmationToastRef.current) {
            branchUpdateConfirmationToastRef.current.show();
        }
    }, []);

    const handleCancel = useCallback(() => {
        setEditingEmail(null);
    }, []);

    const handleEditClick = useCallback((emailType) => {
        if (!iconsDisabled) {
            setEditingEmail(emailType);
        }
    }, [iconsDisabled]);

    const submitSuccessCallback = (updatedAgencyProfile) => {
        setAgencyBranches(prevBranches => prevBranches.map(branch => branch.agencyId === agencyProfile.agencyId ? updatedAgencyProfile : branch));
        branchDeleteConfirmationToastRef.current.show();
        logDetails("Sucessfully updated sweep email for branch:" + updatedAgencyProfile.agencyId);
    };

    const submitFinallyCallback = () => {
        setIsDeleting(false);
    }

    const handleDelete = (formId) => () => {
        // Extract the index from the formId (e.g., "additional-email-form-0" -> 0)
        const index = parseInt(formId.split('-').pop());

        setEmailToDelete(agencyProfile?.sweepDetails?.AdditionalSweepEmails?.[index]?.emailAddress || '');
        setShowDeleteBranchEmailModal(true);
    };

    const handleConfirmDelete = () => {
        if (emailToDelete && agencyProfile?.sweepDetails?.AdditionalSweepEmails) {
            setIsDeleting(true);

            // Create updated agency profile without the deleted email
            const updatedAdditionalEmails = agencyProfile.sweepDetails.AdditionalSweepEmails.filter(
                (email) => email.emailAddress !== emailToDelete
            );

            const updatedAgencyProfile = {
                ...agencyProfile,
                ivans: agencyProfile.ivans ?? {},
                sweepDetails: {
                    ...agencyProfile.sweepDetails,
                    AdditionalSweepEmails: updatedAdditionalEmails
                }
            };

            // Update the agency profile
            try {
                updateSweepDetails(
                    updatedAgencyProfile,
                    () => submitSuccessCallback(updatedAgencyProfile),
                    submitFinallyCallback
                );
            } catch (error) {
                logDetails("Error updating sweep details: " + error.message);
                setIsDeleting(false);
            }

            // Close the modal
            setShowDeleteBranchEmailModal(false);
        }
    };

    const handleDeleteKeyDown = (event) => { };

    // Helper function to determine container opacity
    const getContainerOpacity = useCallback((currentEmailType) => {
        return editingEmail !== null && editingEmail !== currentEmailType ? 0.5 : 1;
    }, [editingEmail]);

    // Helper function to get icon styles
    const getIconStyles = useCallback(() => ({
        opacity: iconsDisabled ? 0.5 : 1,
        pointerEvents: iconsDisabled ? 'none' : 'auto'
    }), [iconsDisabled]);

    // Reset editing state when parent form is canceled
    useEffect(() => {
        setEditingEmail(null);
    }, [resetTrigger]);

    const renderEmailItem = useCallback((emailType, headline, emailAddress, onDelete, isEditable = true, isDeletable = false) => {
        const isExpanded = editingEmail === emailType;

        return (
            <>
                {!isExpanded && (
                    <div>
                        <p className="h4">
                            {headline}
                            {emailType === 'add-new' && (
                                <gds-tooltip>Add an optional email address to receive sweep account notifications. This doesn't replace the primary email on file unless selected</gds-tooltip>
                            )}
                        </p>
                        <span>{emailAddress}</span>
                    </div>
                )}
                {isExpanded ? (
                    <ExpandableFormList setExpandableForm={setBranchEmailExpandableForm}>
                        <ExpandableFormListItem
                            id={`branch-${emailType}-form`}
                            headline={
                                emailType === 'add-new' ? (
                                    <>
                                        {headline}
                                        <gds-tooltip>Add an optional email address to receive sweep account notifications. This doesn't replace the primary email on file unless selected</gds-tooltip>
                                    </>
                                ) : headline
                            }
                            form={<EmailForm
                                onSave={() => handleSave(emailType)}
                                onCancel={handleCancel}
                                agencyProfile={agencyProfile}
                                setAgencyBranches={setAgencyBranches}
                                branchProfile={agencyProfile}
                                expandableForm={branchEmailExpandableForm}
                                isBranch={true}
                                isNewEmail={emailType === 'add-new'}
                                isAdditionalEmail={emailType.startsWith('additional')}
                                additionalEmailIndex={emailType.startsWith('additional') ?
                                    parseInt(emailType.split('-')[1]) : undefined}
                            />}
                        />
                    </ExpandableFormList>
                ) : (
                    <div slot="right-column-content">
                        <div className="right-side">
                            {isDeletable && (
                                <button
                                    type="button"
                                    className="expandable-form-control email-expandable-form-delete-icon geico-icon--small geico-icon--actionable geico-icon icon-trash"
                                    onClick={onDelete}
                                    onKeyDown={handleDeleteKeyDown}
                                    aria-label="Delete item"
                                    tabIndex={0}
                                    style={getIconStyles()}
                                />
                            )}
                            {isEditable && (
                                <button
                                    type="button"
                                    className={"expandable-form-control email-expandable-form-edit-icon geico-icon--small geico-icon--actionable geico-icon icon-edit" + (emailType === 'add-new' ? ' icon-expand' : ' icon-edit')}
                                    onClick={() => handleEditClick(emailType)}
                                    aria-label="Edit item"
                                    tabIndex={0}
                                    style={getIconStyles()}
                                />
                            )}
                        </div>
                    </div>
                )}
            </>
        );
    }, [editingEmail, handleSave, handleCancel, agencyProfile, setAgencyBranches, handleEditClick, getIconStyles]);

    return (
        <div>
            {branchUpdateConfirmationToastRef && (
                <Toast
                    toastRef={branchUpdateConfirmationToastRef}
                    header="Thanks!"
                    description="Email address has been updated"
                    toastType={ToastTypes.Confirmation}
                />
            )}
            {branchAddConfirmationToastRef && (
                <Toast
                    toastRef={branchAddConfirmationToastRef}
                    header="Thanks!"
                    description="Additional email address has been added"
                    toastType={ToastTypes.Confirmation}
                />
            )}
            {branchDeleteConfirmationToastRef && (
                <Toast
                    toastRef={branchDeleteConfirmationToastRef}
                    header="Email Deleted"
                    description="Email address has been removed"
                    toastType={ToastTypes.Confirmation}
                />
            )}
            <ul className="list list--two-column branch-email-expandable-form">
                {/* Primary Email */}
                <li
                    style={{ opacity: getContainerOpacity('primary') }}
                >
                    {renderEmailItem('primary', 'Primary Email', agencyProfile?.sweepDetails?.SweepsEmail)}
                </li>

                {/* Additional Emails */}
                {additionalEmails.map((emailObj, index) => {
                    const emailType = `additional-${index}`;
                    return (
                        <li
                            key={emailObj?.emailAddress || `additional-email-${index}`}
                            style={{ opacity: getContainerOpacity(emailType) }}
                        >
                            {renderEmailItem(
                                emailType,
                                `Additional Email ${index + 1}`,
                                emailObj?.emailAddress,
                                handleDelete(`additional-email-form-${index}`),
                                true,
                                true
                            )}
                        </li>
                    );
                })}

                {/* Add Email */}
                {additionalEmails.length < 5 && (
                    <li
                        className={editingEmail !== 'add-new' ? 'add-email' : ''}
                        style={{ opacity: getContainerOpacity('add-new') }}
                    >
                        {renderEmailItem('add-new', 'Add Email Address', '', undefined, true, false)}
                    </li>
                )}
            </ul>
            <DeleteEmailModal
                emailAddress={emailToDelete}
                showModal={showDeleteBranchEmailModal}
                setShowModal={setShowDeleteBranchEmailModal}
                onConfirm={handleConfirmDelete}
                isLoading={isDeleting}
                modalId={"delete-branch-email-modal"}
            />
        </div>
    );
};

BranchEmailManagement.propTypes = {
    agencyProfile: PropTypes.shape({
        agencyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        ivans: PropTypes.object,
        sweepDetails: PropTypes.shape({
            SweepsEmail: PropTypes.string,
            AdditionalSweepEmails: PropTypes.arrayOf(
                PropTypes.shape({
                    emailAddress: PropTypes.string
                })
            )
        })
    }),
    setAgencyBranches: PropTypes.func,
    resetTrigger: PropTypes.any
};

export default BranchEmailManagement;